import React, { useEffect } from "react";
import { connect, useDispatch } from "react-redux";
import { withRouter } from "react-router-dom";
import loadable from "@loadable/component";

import { getBookmarks, getSets } from "../actions/annotations";
import { getLibraryData } from "../actions/homeLibrary";
import { clearTocCollectionOverrides } from "../actions/reader";
import { setActivePanel } from "../actions/sidePanel";
import { asyncCheckLogin } from "../actions/system";
import {
    selectActivePanel,
    selectAnnotationsOutOfService,
    selectLoggedIn,
    selectContent,
    selectI18nStringById,
    selectLang,
    selectLibrary,
    selectShowArchivedContent,
    selectSortedBookmarks,
} from "../selectors";
import analytics from "../../util/analytics";
import { buildCategoryTypes } from "../../util/type-utils";
import { prependAppBase } from "../../util/uri-utils";

import Loading from "../components/Loading/Loading";

const Home = loadable(
    () =>
        import(
            /* webpackChunkName: 'home' */ "../templates/HomeLibrary/HomeLibrary"
        ),
    { fallback: <Loading /> }
);

const mapStateToProps = (state, ownProps) => {
    let location = ownProps.location;
    let library = selectLibrary(state, location);

    return {
        activePanel: selectActivePanel(state),
        annotationsOutOfService: selectAnnotationsOutOfService(state),
        bookmarks: selectSortedBookmarks(state),
        content: selectContent(state, location),
        dir: state.system.dir,
        homeStatus: state.home.status,
        i18n: state.i18n,
        lang: selectLang(state),
        library,
        location,
        loggedIn: selectLoggedIn(state),
        meta: state.home.meta,
        selectI18nStringById: selectI18nStringById(state),
        showArchivedContent: selectShowArchivedContent(state),
        system: state.system,
    };
};

const mapLangToActions = (dispatchProps, lang) => ({
    getLibraryData: (params, headers) =>
        dispatchProps.getLibraryData({ lang, ...params }, headers),
});

const mergeProps = (stateProps, dispatchProps, ownProps) => {
    const lang = stateProps.lang;

    return {
        ...ownProps,
        ...stateProps,
        ...dispatchProps,
        ...mapLangToActions(dispatchProps, lang),
    };
};

const storeConnector = connect(
    mapStateToProps,
    {
        asyncCheckLogin,
        clearTocCollectionOverrides,
        getBookmarks,
        getLibraryData,
        getSets,
        setActivePanel,
    },
    mergeProps
);

const HomeLibraryContainer = (props) => {
    const {
        asyncCheckLogin,
        getLibraryData,
        lang,
        library,
        annotationsOutOfService,
        crossLinkMode,
        getBookmarks,
        getSets,
        loggedIn,
    } = props;

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(clearTocCollectionOverrides());
        getSecureData();

        if (!library.loaded) {
            getLibraryData({ uri: prependAppBase("/"), lang });
        }
        // Note: Disabled below is to cause this useEffect to run only after the initial render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (library.uri && library.loaded) {
            firePageViewEvent();
        }

        // Note: Disabled below... this useEffect will only run when the library.uri or library.loaded change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [library.uri, library.loaded]);

    const firePageViewEvent = () => {
        const currentBreadcrumbs = library.breadCrumbs.map(({ uri }) =>
            uri.split("/").pop()
        );

        if (currentBreadcrumbs.length && !crossLinkMode && library.loaded) {
            analytics.firePageViewEvent(asyncCheckLogin, {
                page: {
                    info: {
                        breadcrumbs: library.breadCrumbs.map(({ uri }) =>
                            uri.split("/").pop()
                        ),
                        language: lang,
                    },
                    category: {
                        ...buildCategoryTypes(library.uri),
                        type: "navigation",
                    },
                },
            });
        }
    };

    const getSecureData = async () => {
        const annotationAvailable = !annotationsOutOfService && !crossLinkMode;

        if ((loggedIn || (await asyncCheckLogin())) && annotationAvailable) {
            getBookmarks();
            getSets();
        }
    };

    return <Home {...props} />;
};

HomeLibraryContainer.updateInitialStore = async (store, uri) => {
    let state = store.getState(),
        context = state.preview ? "preview" : "published",
        dispatch = store.dispatch,
        lang = selectLang(state),
        headers = { cookie: state.headers.cookie };

    let actions = [getLibraryData({ context, lang, uri }, headers)];

    await Promise.all(
        actions.map((action) => action(dispatch, store.getState))
    );
};

export default withRouter(storeConnector(HomeLibraryContainer));
